import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Check, Loader2, Pencil, Star, Trash2, X } from 'lucide-react';
import { useBookmarks, useRemoveBookmark, useRenameBookmark, useReorderBookmarks, type Bookmark } from '@/api/bookmarks';

/** Account → Bookmarks: rename, reorder and remove your saved pages. */
export default function AccountBookmarksPage() {
  const { data, isLoading } = useBookmarks();
  const reorder = useReorderBookmarks();
  const items = data?.bookmarks ?? [];
  const move = (i: number, d: -1 | 1) => {
    const ids = items.map((b) => b.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    reorder.mutate(ids);
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-lg font-semibold text-foreground">Bookmarks</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Pages you've saved with the <Star className="inline h-3.5 w-3.5 align-[-2px]" /> in the top bar. They appear under Bookmarks in the sidebar, in this order. Filtered views and record pages can be bookmarked too.
      </p>
      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-card">
        {isLoading ? <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : items.length === 0 ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">No bookmarks yet. Open any page and click the star in the top bar.</div>
          : (
            <ul className="divide-y divide-divider">
              {items.map((b, i) => <Row key={b.id} b={b} first={i === 0} last={i === items.length - 1} onMove={(d) => move(i, d)} />)}
            </ul>
          )}
      </div>
    </div>
  );
}

function Row({ b, first, last, onMove }: { b: Bookmark; first: boolean; last: boolean; onMove: (d: -1 | 1) => void }) {
  const rename = useRenameBookmark();
  const remove = useRemoveBookmark();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(b.title);
  const save = () => {
    if (!title.trim() || title.trim() === b.title) { setEditing(false); setTitle(b.title); return; }
    rename.mutate({ id: b.id, title: title.trim() }, { onSuccess: () => setEditing(false), onError: (e) => toast.error(e instanceof Error ? e.message : 'Rename failed') });
  };
  return (
    <li className="flex items-center gap-2 px-4 py-2">
      <div className="min-w-0 flex-1">
        {editing ? (
          <input autoFocus value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setTitle(b.title); } }}
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-ring" />
        ) : <Link to={b.path} className="block truncate text-sm font-medium text-foreground hover:underline">{b.title}</Link>}
        <div className="truncate text-[11px] text-muted-foreground">{b.path}</div>
      </div>
      {editing
        ? <><Btn label="Save" onClick={save}><Check className="h-3.5 w-3.5" /></Btn><Btn label="Cancel" onClick={() => { setEditing(false); setTitle(b.title); }}><X className="h-3.5 w-3.5" /></Btn></>
        : <Btn label="Rename" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5" /></Btn>}
      <Btn label="Move up" disabled={first} onClick={() => onMove(-1)}><ArrowUp className="h-3.5 w-3.5" /></Btn>
      <Btn label="Move down" disabled={last} onClick={() => onMove(1)}><ArrowDown className="h-3.5 w-3.5" /></Btn>
      <Btn label="Remove" onClick={() => remove.mutate(b.id)} danger><Trash2 className="h-3.5 w-3.5" /></Btn>
    </li>
  );
}

function Btn({ label, onClick, disabled, danger, children }: { label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      className={`rounded p-1.5 text-muted-foreground hover:bg-muted disabled:opacity-30 ${danger ? 'hover:text-destructive' : 'hover:text-foreground'}`}>{children}</button>
  );
}
